import { Box, Card, CardActionArea, Stack } from '@mui/material'
import { useTheme, LeafFlexMediaCard } from '@thriveglobal/thrive-web-leafkit'
import { useCallback, useEffect, useRef } from 'react'
import {
    ifEnter,
    PLACEHOLDER_ICON_IMAGE,
    useGetImageWidth
} from '../../../utils'
import { CardDetails } from './'

export type ShortContentCardProps = {
    title?: string
    subTitle?: string
    imageSrc?: string
    rootMargin?: string
    root?: null | Element
    threshold?: number
    responsive?: boolean
    onClick?: () => void
    onEnter?: (entry: IntersectionObserverEntry) => void
    role?: string
    contentWrapper?: (children: React.ReactNode) => React.ReactNode
    actions?: React.ReactNode
    hasViewMoreAction?: boolean
    onViewMore?: () => void
}

export default function ShortContentCard(props: ShortContentCardProps) {
    const {
        title,
        subTitle,
        imageSrc,
        rootMargin,
        root,
        threshold,
        responsive,
        onClick,
        onEnter,
        role,
        contentWrapper,
        actions,
        hasViewMoreAction,
        onViewMore
    } = props

    const theme = useTheme()

    const ref = useRef<HTMLDivElement>(null)

    const image = imageSrc ? imageSrc : PLACEHOLDER_ICON_IMAGE

    const imageWidth = useGetImageWidth(image, 60, 60)

    const handleOnClick = useCallback(() => {
        if (onClick) {
            onClick()
        }
    }, [onClick])

    useEffect(() => {
        if (!onEnter || !ref.current) return

        const el = ref.current

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) onEnter(entry)
            },
            { rootMargin, threshold, root }
        )

        observer.observe(el)

        return () => observer.unobserve(el)
    }, [onEnter, root, rootMargin, threshold])

    const renderActions = () => {
        if (!actions) return null

        return (
            <Stack
                direction="row"
                spacing={2}
                onClick={(e) => e.stopPropagation()}
            >
                {actions}
            </Stack>
        )
    }

    const renderContent = () => {
        return (
            <Stack direction="column" width="100%" height="100%" gap={2}>
                <Box
                    sx={{
                        height: 60,
                        width: imageWidth,
                        background: `url(${image}) no-repeat left`,
                        backgroundSize: 'cover',
                        flexShrink: 0
                    }}
                ></Box>

                <CardDetails
                    title={title}
                    subTitle={subTitle}
                    subTitleLineClamp={3}
                    hasViewMoreAction={hasViewMoreAction}
                    onViewMore={onViewMore}
                    bottomRightContent={renderActions()}
                />
            </Stack>
        )
    }

    return (
        <Card
            ref={ref}
            component="div"
            role={role}
            sx={{
                width: responsive ? '100%' : 341,
                height: '100%',
                alignItems: 'flex-start',
                boxShadow: theme.shadows[9]
            }}
        >
            <CardActionArea
                onClick={handleOnClick}
                onKeyUp={ifEnter(handleOnClick)}
                tabIndex={0}
                sx={{
                    height: '100%',
                    width: '100%',
                    p: 3
                }}
            >
                {contentWrapper
                    ? contentWrapper(renderContent())
                    : renderContent()}
            </CardActionArea>
        </Card>
    )
}
